import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type {
  ChartAnnotation,
  ChartAnnotationCreate,
  ChartAnnotationUpdate,
} from '@/types/chartAnnotation';

/**
 * Hook to fetch chart annotations for a tank with optional filtering
 */
export function useChartAnnotations(
  tankId: string | undefined,
  options?: {
    startDate?: Date;
    endDate?: Date;
    parameter?: string;
  }
) {
  return useQuery({
    queryKey: [
      'chartAnnotations',
      tankId,
      options?.startDate?.toISOString(),
      options?.endDate?.toISOString(),
      options?.parameter,
    ],
    queryFn: async () => {
      if (!tankId) return [];
      const params = new URLSearchParams();
      if (options?.startDate) {
        params.append('start_date', options.startDate.toISOString());
      }
      if (options?.endDate) {
        params.append('end_date', options.endDate.toISOString());
      }
      if (options?.parameter) {
        params.append('parameter', options.parameter);
      }

      return api.get<ChartAnnotation[]>(
        `/api/tanks/${tankId}/annotations?${params}`
      );
    },
    enabled: !!tankId,
  });
}

/**
 * Hook to create a new chart annotation
 */
export function useCreateAnnotation(tankId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (annotation: ChartAnnotationCreate) => {
      if (!tankId) throw new Error('No tank selected');
      return api.post<ChartAnnotation>(
        `/api/tanks/${tankId}/annotations`,
        annotation
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartAnnotations', tankId] });
    },
  });
}

/**
 * Hook to update an existing chart annotation
 */
export function useUpdateAnnotation(tankId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...annotation
    }: ChartAnnotationUpdate & { id: number }) => {
      if (!tankId) throw new Error('No tank selected');
      return api.put<ChartAnnotation>(
        `/api/tanks/${tankId}/annotations/${id}`,
        annotation
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartAnnotations', tankId] });
    },
  });
}

/**
 * Hook to delete a chart annotation
 */
export function useDeleteAnnotation(tankId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (annotationId: number) => {
      if (!tankId) throw new Error('No tank selected');
      await api.delete(`/api/tanks/${tankId}/annotations/${annotationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartAnnotations', tankId] });
    },
  });
}
